/**
 * Page de suivi des demandes d'activation à distance soumises par l'étudiant.
 * Charte sobre, cohérente avec le reste du portail.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Inbox, Clock, CheckCircle2, XCircle, AlertCircle,
  Upload, Loader2, Image as ImageIcon, Trash2,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, DemandeActivationItem } from '../services/portailApi';

const PERIODE_LABELS: Record<string, string> = {
  INSC: 'Inscription / Réinscription', NOV: 'Novembre', DEC: 'Décembre', JAN: 'Janvier',
  FEV: 'Février', MAR: 'Mars', AVR: 'Avril', MAI: 'Mai',
  JUIN: 'Juin', JUIL: 'Juillet', AOUT: 'Août', EXAM: 'Examen',
};

const TYPE_LABELS: Record<string, string> = {
  paiement: 'Reçu de paiement', gratuite: 'Gratuité', bourse: 'Bourse', autre: 'Autre',
};

const STATUT_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  en_attente: {
    label: 'En attente',
    icon: <Clock size={14} />,
    color: 'text-orange-300',
    bg: 'bg-orange-900/20 border-orange-800/50',
  },
  complement_demande: {
    label: 'Complément demandé',
    icon: <AlertCircle size={14} />,
    color: 'text-blue-300',
    bg: 'bg-blue-900/20 border-blue-800/50',
  },
  acceptee: {
    label: 'Acceptée',
    icon: <CheckCircle2 size={14} />,
    color: 'text-green-300',
    bg: 'bg-green-900/20 border-green-800/50',
  },
  rejetee: {
    label: 'Rejetée',
    icon: <XCircle size={14} />,
    color: 'text-red-300',
    bg: 'bg-red-900/20 border-red-800/50',
  },
  // 2026-09-13 : paiement enregistré par la caisse, la demande n'est plus nécessaire
  cloturee: {
    label: 'Clôturée — vous êtes à jour',
    icon: <CheckCircle2 size={14} />,
    color: 'text-emerald-300',
    bg: 'bg-emerald-900/10 border-emerald-800/40',
  },
};

export default function MesDemandesActivation() {
  const navigate = useNavigate();
  const [demandes, setDemandes] = useState<DemandeActivationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [complementFor, setComplementFor] = useState<DemandeActivationItem | null>(null);
  const [complementPhoto, setComplementPhoto] = useState<File | null>(null);
  const [complementMsg, setComplementMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DemandeActivationItem | null>(null);

  const supprimer = async (d: DemandeActivationItem) => {
    setDeletingId(d.id);
    setFeedback(null);
    try {
      await portailApi.supprimerDemandeActivation(d.id);
      setFeedback({ type: 'success', text: 'Demande supprimée.' });
      setConfirmDelete(null);
      charger();
    } catch (e: any) {
      setFeedback({ type: 'error', text: e?.message || 'Suppression impossible.' });
    } finally {
      setDeletingId(null);
    }
  };

  const charger = () => {
    setLoading(true);
    portailApi.mesDemandesActivation()
      .then((r) => setDemandes(r.results))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { charger(); }, []);

  const sendComplement = async () => {
    if (!complementFor || !complementPhoto) return;
    setSending(true);
    setFeedback(null);
    try {
      await portailApi.repondreComplementDemande(
        complementFor.id, complementPhoto, complementMsg.trim() || undefined,
      );
      setFeedback({ type: 'success', text: 'Complément envoyé. La caisse va revoir votre demande.' });
      setComplementFor(null);
      setComplementPhoto(null);
      setComplementMsg('');
      charger();
    } catch (e: any) {
      setFeedback({ type: 'error', text: e?.message || 'Erreur d\'envoi' });
    } finally {
      setSending(false);
    }
  };

  return (
    <PortailLayout showHeader title="Mes demandes d'activation">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/portail/accueil')}
            className="p-2 hover:bg-gray-800 rounded text-gray-300"
          >
            <ArrowLeft size={18} />
          </button>
          <h2 className="font-semibold text-gray-100 text-sm flex-1">
            Suivi de vos demandes
          </h2>
        </div>

        {feedback && (
          <div className={`p-3 rounded text-xs border ${
            feedback.type === 'success'
              ? 'bg-green-900/30 border-green-800 text-green-300'
              : 'bg-red-900/30 border-red-800 text-red-300'
          }`}>{feedback.text}</div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="inline-block animate-spin w-6 h-6 text-blue-500" />
          </div>
        ) : demandes.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
            <Inbox className="w-10 h-10 mx-auto text-gray-500 mb-2" />
            <p className="text-sm text-gray-300">Aucune demande envoyée</p>
            <button
              onClick={() => navigate('/portail/accueil')}
              className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors"
            >
              Retour à l'accueil
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {demandes.map((d) => {
              const cfg = STATUT_CONFIG[d.statut] || STATUT_CONFIG.en_attente;
              const periodes = (d.periodes_concernees || []).map((p) => PERIODE_LABELS[p] || p);
              return (
                <div key={d.id} className={`border rounded-lg p-3 ${cfg.bg}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${cfg.color}`}>
                        {cfg.icon} {cfg.label}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {new Date(d.date_creation).toLocaleString('fr-FR')}
                      </span>
                    </div>
                    {(d.statut === 'en_attente' || d.statut === 'complement_demande') && (
                      <button
                        onClick={() => setConfirmDelete(d)}
                        disabled={deletingId === d.id}
                        title="Supprimer cette demande"
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                      >
                        {deletingId === d.id
                          ? <Loader2 size={14} className="animate-spin" />
                          : <Trash2 size={14} />}
                      </button>
                    )}
                  </div>

                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs text-gray-400">Type :</span>
                      <span className="text-gray-100">{TYPE_LABELS[d.type_demande] || d.type_demande}</span>
                    </div>
                    {periodes.length > 0 && (
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-xs text-gray-400">Périodes :</span>
                        <span className="text-gray-100 text-xs">{periodes.join(', ')}</span>
                      </div>
                    )}
                    {d.montant_declare && (
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-gray-400">Montant déclaré :</span>
                        <span className="text-gray-100">{d.montant_declare} FCFA</span>
                      </div>
                    )}
                    {d.reference_recu && (
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-gray-400">N° reçu :</span>
                        <span className="text-gray-100 font-mono text-xs">{d.reference_recu}</span>
                      </div>
                    )}
                    {d.photo_url && (
                      <a
                        href={d.photo_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:underline"
                      >
                        <ImageIcon size={12} /> Voir la photo envoyée
                      </a>
                    )}
                  </div>

                  {/* Réponse caisse */}
                  {d.message_caisse && (
                    <div className="mt-2 pt-2 border-t border-gray-700/50 text-xs">
                      <div className="text-gray-400 mb-0.5">
                        Caisse {d.traite_par_nom ? `(${d.traite_par_nom})` : ''} :
                      </div>
                      <div className="text-gray-200 whitespace-pre-wrap">{d.message_caisse}</div>
                    </div>
                  )}
                  {d.statut === 'rejetee' && d.motif_rejet && (
                    <div className="mt-2 pt-2 border-t border-red-800/50 text-xs">
                      <div className="text-red-400 mb-0.5">Motif du rejet :</div>
                      <div className="text-red-200">{d.motif_rejet}</div>
                    </div>
                  )}
                  {d.statut === 'acceptee' && (d.periodes_validees || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-green-800/50 text-xs">
                      <div className="text-green-400">
                        Périodes validées : {d.periodes_validees.map((p) => PERIODE_LABELS[p] || p).join(', ')}
                      </div>
                    </div>
                  )}

                  {/* Action si complément demandé */}
                  {d.statut === 'complement_demande' && (
                    <div className="mt-3">
                      <button
                        onClick={() => setComplementFor(d)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors"
                      >
                        <Upload size={12} /> Envoyer le complément demandé
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmation suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3">
          <div className="bg-gray-900 border border-gray-800 rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-gray-800">
              <h3 className="font-semibold text-gray-100 text-sm inline-flex items-center gap-2">
                <Trash2 size={16} className="text-red-400" /> Supprimer la demande ?
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-300">
                Cette demande sera définitivement supprimée. La photo envoyée sera aussi retirée du serveur.
              </p>
              <p className="text-xs text-gray-400">
                Type : <span className="text-gray-200">{TYPE_LABELS[confirmDelete.type_demande] || confirmDelete.type_demande}</span>
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-3 py-1.5 text-xs text-gray-300 hover:text-gray-100"
                >Annuler</button>
                <button
                  onClick={() => supprimer(confirmDelete)}
                  disabled={deletingId === confirmDelete.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-medium rounded"
                >
                  {deletingId === confirmDelete.id
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Trash2 size={12} />}
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale envoi complément */}
      {complementFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3">
          <div className="bg-gray-900 border border-gray-800 rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="font-semibold text-gray-100 text-sm">Envoyer un complément</h3>
              <button onClick={() => setComplementFor(null)} className="text-gray-400 hover:text-gray-200">×</button>
            </div>
            <div className="p-5 space-y-3">
              {complementFor.message_caisse && (
                <div className="p-3 bg-blue-900/20 border border-blue-800/50 rounded text-xs text-blue-200">
                  <div className="font-medium mb-1">Demande de la caisse :</div>
                  <div className="whitespace-pre-wrap">{complementFor.message_caisse}</div>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  Nouvelle photo <span className="text-red-400">*</span>
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={(e) => setComplementPhoto(e.target.files?.[0] || null)}
                  className="block w-full text-xs text-gray-300 file:mr-2 file:py-1.5 file:px-3 file:border-0 file:rounded file:bg-blue-600 file:text-white file:text-xs"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Message (facultatif)</label>
                <textarea
                  value={complementMsg}
                  onChange={(e) => setComplementMsg(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-100 text-xs"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setComplementFor(null)}
                  className="px-3 py-1.5 text-xs text-gray-300 hover:text-gray-100"
                >Annuler</button>
                <button
                  onClick={sendComplement}
                  disabled={sending || !complementPhoto}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded"
                >
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PortailLayout>
  );
}
